import { useQueries } from "@tanstack/react-query";
import { facilityApi } from "../api";
import type { FacilityResponse } from "../types/generated";

/**
 * Resolve a set of facility ids to their REAL display names.
 *
 * Several cross-facility lists (the player's cross-facility bookings calendar,
 * the player's ranked match history) carry only a `facility_id` — no facility
 * NAME. Rather than render a raw UUID or, worse, fabricate a name, this resolves
 * each distinct facility id to its real `name` via the existing facility endpoint
 * (`facilityApi.get` → `FacilityResponse.name`). One query per distinct facility,
 * cached under the shared `booking.facility` key tuple so it is shared with the
 * per-facility booking flow.
 *
 * Returns a `Map<facilityId, name>` populated only for facilities that have
 * resolved successfully. A facility still loading (or that errored) is simply
 * absent from the map; the caller falls back to an honest neutral label — it
 * never invents a name.
 *
 * Promoted out of `modules/calendar` into `@bw/shared` so both the calendar and
 * the compete (match-history) journeys consume one authoritative resolver (module
 * isolation — no sibling import; clean migration — no duplicate / dual path).
 * It depends only on `facilityApi` + react-query, both already in shared.
 *
 * The query key is inlined as a literal tuple (NOT imported from any app's
 * query-key factory) so this shared hook has no reverse dependency on a consuming
 * app — mirroring `useWaiverGate`. It matches the player app's
 * `booking.facility(id)` slot exactly so the cache stays shared.
 */
export function useFacilityNames(facilityIds: string[]): Map<string, string> {
  const results = useQueries({
    queries: facilityIds.map((id) => ({
      queryKey: ["booking", "facility", id] as const,
      queryFn: () => facilityApi.get(id),
      // A facility name rarely changes — keep it warm so opening the calendar
      // repeatedly does not refetch every venue.
      staleTime: 1000 * 60 * 5,
    })),
  });

  const names = new Map<string, string>();
  results.forEach((result, i) => {
    const facility = result.data as FacilityResponse | undefined;
    const name = facility?.name;
    if (name) names.set(facilityIds[i], name);
  });
  return names;
}
